import { initializeApp } from 'firebase/app';
import { getFirestore, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyBtGcZC0i5qJrJBna33GGdOxekRYYAHZJs",
  authDomain: "anchorline-c36c9.firebaseapp.com",
  projectId: "anchorline-c36c9",
  storageBucket: "anchorline-c36c9.firebasestorage.app",
  messagingSenderId: "733831216780",
  appId: "1:733831216780:web:3d4820d83bde857cf9c5a9",
  measurementId: "G-GY8RZ3W8VK"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const database = getFirestore(app);  // ✅ Cloud Firestore
// ============================================================================
// SECURITY & VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validates shipment number format to prevent injection attacks
 * Expected format: GS followed by 9 numeric digits (e.g., GS123456789)
 */
const validateShipmentNumber = (shipmentNumber) => {
  if (!shipmentNumber || typeof shipmentNumber !== 'string') {
    throw new Error('Shipment number must be a non-empty string');
  }
  
  // Remove whitespace and convert to uppercase
  const cleaned = shipmentNumber.trim().toUpperCase();
  
  // Validate format: GS followed by 9 digits
  const shipmentRegex = /^GS\d{9}$/;
  if (!shipmentRegex.test(cleaned)) {
    throw new Error('Invalid shipment number format. Expected: GS followed by 9 digits (e.g., GS123456789)');
  }
  
  return cleaned;
};

/**
 * Sanitizes string input to prevent injection and XSS attacks
 */
const sanitizeString = (input, maxLength = 100) => {
  if (typeof input !== 'string') {
    throw new Error('Input must be a string');
  }
  
  // Remove dangerous characters and trim
  let sanitized = input.trim();
  
  // Remove potential script tags and dangerous patterns
  sanitized = sanitized.replace(/<script[^>]*>.*?<\/script>/gi, '');
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  sanitized = sanitized.replace(/[<>']/g, '');
  
  // Enforce length limit
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  
  if (sanitized.length === 0) {
    throw new Error('Input cannot be empty after sanitization');
  }
  
  return sanitized;
};

/**
 * Validates tracking status to prevent invalid data
 */
const validateStatus = (status) => {
  const validStatuses = ['Pending', 'Processing', 'In Transit', 'Out for Delivery', 'Delivered'];
  
  if (!validStatuses.includes(status)) {
    throw new Error(`Invalid status. Must be one of: ${validStatuses.join(', ')}`);
  }
  
  return status;
};

/**
 * Validates that tracking data has all required fields
 * Required fields: status, currentLocation, origin, destination, weight, estimatedDelivery, lastUpdate, timeline
 */
const validateTrackingData = (data) => {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid tracking data format');
  }
  
  const requiredFields = ['status', 'currentLocation', 'origin', 'destination', 'weight', 'estimatedDelivery', 'lastUpdate', 'timeline'];
  const missingFields = requiredFields.filter(field => !(field in data));
  
  if (missingFields.length > 0) {
    throw new Error(`Incomplete tracking data. Missing fields: ${missingFields.join(', ')}`);
  }
  
  // Validate timeline is an array
  if (!Array.isArray(data.timeline)) {
    throw new Error('Timeline must be an array');
  }
  
  if (data.timeline.length === 0) {
    throw new Error('Timeline cannot be empty');
  }
  
  return true;
};

/**
 * Rate limiting helper - tracks calls per user/IP to prevent abuse
 */
const rateLimitMap = new Map();

const checkRateLimit = (identifier, maxRequests = 10, timeWindowMs = 60000) => {
  const now = Date.now();
  
  if (!rateLimitMap.has(identifier)) {
    rateLimitMap.set(identifier, []);
  }
  
  const requests = rateLimitMap.get(identifier);
  
  // Remove old requests outside the time window
  const recentRequests = requests.filter(timestamp => now - timestamp < timeWindowMs);
  rateLimitMap.set(identifier, recentRequests);
  
  if (recentRequests.length >= maxRequests) {
    throw new Error('Too many requests. Please try again later.');
  }
  
  recentRequests.push(now);
  return true;
};

/**
 * Audit logging - logs admin actions for security tracking
 */
const logAuditEvent = async (action, details) => {
  try {
    const timestamp = Date.now();
    const auditEntry = {
      timestamp,
      action,
      details,
      userAgent: navigator.userAgent.substring(0, 100),
      timestamp_readable: new Date().toISOString()
    };
    
    // Log to console in development
    console.log('[AUDIT LOG]', auditEntry);
    
    // Optional: Send to backend for permanent storage
    // await fetch('/api/audit-log', { method: 'POST', body: JSON.stringify(auditEntry) });
  } catch (error) {
    console.warn('Failed to log audit event:', error);
  }
};
// ============================================================================
// DATABASE OPERATIONS
// ============================================================================

/**
 * Initialize sample tracking data - call this once to populate Firestore
 * Run from browser console: initializeSampleData()
 */
export const initializeSampleData = async () => {
  const sampleData = {
    'GS123456789': {
      status: 'In Transit',
      currentLocation: 'Los Angeles Distribution Center',
      origin: 'New York, NY',
      destination: 'San Francisco, CA',
      estimatedDelivery: 'Feb 24, 2026',
      weight: '2.5 kg',
      lastUpdate: 'Feb 22, 2026 - 2:30 PM',
      timeline: [
        { date: 'Feb 20, 2026', time: '9:00 AM', status: 'Picked Up', location: 'New York, NY' },
        { date: 'Feb 21, 2026', time: '3:15 PM', status: 'Arrived at Hub', location: 'Chicago, IL' },
        { date: 'Feb 22, 2026', time: '2:30 PM', status: 'In Transit', location: 'Los Angeles, CA' },
        { date: 'Feb 24, 2026', time: 'Est. 11:00 AM', status: 'Out for Delivery', location: 'San Francisco, CA' }
      ]
    },
    'GS987654321': {
      status: 'Delivered',
      currentLocation: 'Miami, FL',
      origin: 'London, UK',
      destination: 'Miami, FL',
      estimatedDelivery: 'Feb 20, 2026',
      weight: '5.0 kg',
      lastUpdate: 'Feb 20, 2026 - 4:45 PM',
      timeline: [
        { date: 'Feb 17, 2026', time: '8:00 AM', status: 'Picked Up', location: 'London, UK' },
        { date: 'Feb 18, 2026', time: '11:30 AM', status: 'Customs Cleared', location: 'JFK Airport, NY' },
        { date: 'Feb 19, 2026', time: '2:00 PM', status: 'In Transit', location: 'Atlanta, GA' },
        { date: 'Feb 20, 2026', time: '4:45 PM', status: 'Delivered', location: 'Miami, FL' }
      ]
    },
    'GS555444333': {
      status: 'Processing',
      currentLocation: 'Secure Vault - Dubai',
      origin: 'Dubai, UAE',
      destination: 'Singapore',
      estimatedDelivery: 'Feb 25, 2026',
      weight: '10.0 kg',
      lastUpdate: 'Feb 22, 2026 - 10:00 AM',
      timeline: [
        { date: 'Feb 22, 2026', time: '10:00 AM', status: 'Processing', location: 'Dubai, UAE' }
      ]
    }
  };

  try {
    // ✅ Add each document to Firestore shipments collection
    for (const [shipmentId, shipmentData] of Object.entries(sampleData)) {
      const docRef = doc(database, 'shipments', shipmentId);
      await setDoc(docRef, shipmentData);
      console.log(`✅ Added ${shipmentId} to Firestore`);
    }
    
    console.log('✅ All sample data initialized in Firestore');
    await logAuditEvent('INIT_SAMPLE_DATA', 'Sample tracking data initialized');
  } catch (error) {
    console.error('❌ Error initializing Firestore sample data:', error);
    throw error;
  }
};

/**
 * PUBLIC: Get tracking info by shipment number from Firestore
 * Anyone can call this to check shipment status (read-only)
 * 
 * @param {string} shipmentNumber - Shipment ID (e.g., "GS123456789")
 * @returns {object} Tracking information or null if not found
 */
export const getTrackingInfo = async (shipmentNumber) => {
  try {
    // Rate limiting - prevent excessive queries
    checkRateLimit('tracking-reads', 50, 60000); // 50 reads per minute
    
    // Validate and sanitize input
    const validatedShipmentNumber = validateShipmentNumber(shipmentNumber);
    
    console.log(`🔍 Querying Firestore for: ${validatedShipmentNumber}`);
    
    // ✅ Query Firestore collection
    const docRef = doc(database, 'shipments', validatedShipmentNumber);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      
      // Validate that all required fields are present
      try {
        validateTrackingData(data);
      } catch (validationError) {
        console.error('❌ Data validation error:', validationError.message);
        throw validationError;
      }
      
      // Log successful retrieval
      await logAuditEvent('GET_TRACKING', { shipmentNumber: validatedShipmentNumber });
      
      return data;
    } else {
      console.warn(`⚠️ Shipment not found in Firestore: ${validatedShipmentNumber}`);
      return null;
    }
  } catch (error) {
    if (error.message.includes('Too many requests')) {
      console.error('❌ Rate limit exceeded:', error.message);
    } else if (error.message.includes('Invalid shipment')) {
      console.error('❌ Validation error:', error.message);
    } else {
      console.error('❌ Error fetching from Firestore:', error);
    }
    return null;
  }
};

/**
 * ADMIN ONLY: Update tracking status
 * Must be called from authenticated admin session
 * 
 * @param {string} shipmentNumber - Shipment ID
 * @param {string} newStatus - New status (Pending, Processing, In Transit, Out for Delivery, Delivered)
 * @param {string} location - Current location
 */
export const updateTrackingStatus = async (shipmentNumber, newStatus, location) => {
  try {
    // Rate limiting - prevent update spam
    checkRateLimit('tracking-updates', 20, 60000); // 20 updates per minute
    
    // Validate all inputs
    const validatedShipmentNumber = validateShipmentNumber(shipmentNumber);
    const validatedStatus = validateStatus(newStatus);
    const validatedLocation = sanitizeString(location, 100);
    
    console.log(`📝 Updating Firestore: ${validatedShipmentNumber} → ${validatedStatus}`);
    
    // ✅ Get current document from Firestore
    const docRef = doc(database, 'shipments', validatedShipmentNumber);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      
      // Create updated data
      const now = new Date();
      const newTimeline = data.timeline || [];
      newTimeline.push({
        date: now.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }),
        time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        status: validatedStatus,
        location: validatedLocation
      });
      
      // ✅ Update in Firestore
      await updateDoc(docRef, {
        status: validatedStatus,
        currentLocation: validatedLocation,
        lastUpdate: now.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }),
        timeline: newTimeline
      });
      
      // Audit log
      await logAuditEvent('UPDATE_TRACKING', {
        shipmentNumber: validatedShipmentNumber,
        newStatus: validatedStatus,
        location: validatedLocation,
        timestamp: new Date().toISOString()
      });
      
      console.log('✅ Tracking status updated in Firestore');
      return { success: true, message: 'Tracking updated' };
    } else {
      throw new Error(`Shipment ${validatedShipmentNumber} not found in Firestore`);
    }
  } catch (error) {
    console.error('❌ Error updating Firestore:', error.message);
    
    await logAuditEvent('UPDATE_TRACKING_FAILED', {
      error: error.message,
      timestamp: new Date().toISOString()
    });
    
    throw error;
  }
};

/**
 * ADMIN ONLY: Add a new shipment to Firestore tracking
 */
export const addNewShipment = async (shipmentNumber, originLocation, destinationLocation, weight) => {
  try {
    checkRateLimit('shipment-creation', 10, 60000); // 10 new shipments per minute
    
    const validatedShipmentNumber = validateShipmentNumber(shipmentNumber);
    const validatedOrigin = sanitizeString(originLocation);
    const validatedDestination = sanitizeString(destinationLocation);
    const validatedWeight = sanitizeString(weight, 20);
    
    const now = new Date();
    const newShipment = {
      status: 'Processing',
      currentLocation: validatedOrigin,
      origin: validatedOrigin,
      destination: validatedDestination,
      weight: validatedWeight,
      estimatedDelivery: 'TBD',
      lastUpdate: now.toLocaleString(),
      timeline: [
        {
          date: now.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }),
          time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          status: 'Processing',
          location: validatedOrigin
        }
      ]
    };
    
    // ✅ Add to Firestore
    const docRef = doc(database, 'shipments', validatedShipmentNumber);
    await setDoc(docRef, newShipment);
    
    await logAuditEvent('CREATE_SHIPMENT', {
      shipmentNumber: validatedShipmentNumber,
      origin: validatedOrigin,
      destination: validatedDestination
    });
    
    console.log('✅ New shipment created in Firestore');
    return { success: true, shipmentNumber: validatedShipmentNumber };
  } catch (error) {
    console.error('❌ Error creating shipment in Firestore:', error.message);
    throw error;
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

export { database };
